/* eslint-disable no-shadow */
/* eslint-disable no-unused-vars */
/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2023. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useCallback } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { ProfileModel, Authentication } from '@spm/universal-access';
import { withRouter, Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Header,
  PrimaryNavigation,
  SecondaryNavigation,
  Tabs,
  Tab,
} from '@govhhs/govhhs-design-system-react';
import PATHS from '@spm/universal-access-ui/src/router/Paths';
import logo from '../../../dist/img/CCLogo.png';
import translations from './ApplicationHeaderMessages';
import SignInMenuComponent from './SignInMenuComponent';
import * as ApplicationHeaderLogic from './ApplicationHeaderLogic';
import { ID, NAVIGATION_HEADER_TABS } from '../constants';
import Paths from '../../../paths';

/**
 * Returns true if the Your Account tab is active for the current url pathname. If
 * true the primary navigation contains specific navigation tabs.
 *
 * @param {*} urlPathname current url pathname.
 * @returns true if tab should be active.
 */
const isYourAccountTabActive = urlPathname =>
  ApplicationHeaderLogic.isTabActiveForUrlPathname(
    urlPathname,
    NAVIGATION_HEADER_TABS.YOUR_ACCOUNT.NAME
  );

const loginAsPublicCitizenCallback = () => {
  // console.log('Logging in as public citizen')
};

/**
 * Returns the ApplicationHeaderComponent.
 *
 * @param {*} props props passed to this component.
 * @returns {Component} ApplicationHeaderComponent.
 */
const ApplicationHeaderComponent = props => {
  const {
    intl,
    location,
    profile: userProfile,
    hasErrorBeenCaught,
    history,
    isUserCanAppeal,
    isAppealsEnabled,
    isALinkedOrStandardUser,
    isEmpty,
  } = props;

  const { formatMessage } = intl;
  const { pathname: currentUrlPathname } = location;
  const { getActivePrimaryNavigationTabId, getActiveSecondaryNavigationTabId } =
    ApplicationHeaderLogic;

  const marginDirection = document.dir === 'rtl' ? { marginRight: 'auto' } : { marginLeft: 'auto' };

  const loginAsPublicCitizenCallback = useCallback(() => {
    // '---LoginAsPublicCitizenCallback---'

    if (location.pathname !== PATHS.HOME) {
      // 'Not on homepage, push the home page path'
      history.push(PATHS.HOME);
    } else {
      // 'already on homepage, refresh manually'
      window.location.reload(true);
    }
  });

  const appealsTab = isUserCanAppeal ? (
    <Tab
      data-testid={NAVIGATION_HEADER_TABS.APPEALS.ID}
      id={NAVIGATION_HEADER_TABS.APPEALS.ID}
      label={formatMessage(translations.headerAppealsLabel)}
      link={<RouterLink to={PATHS.APPEALS.ROOT} />}
    />
  ) : (
    ''
  );

  // while Activate citizen account flow.
  const isCitizenAccountActivation = /activate-account/.test(props.location.pathname);

  // Unlock Account  flow.
  const isCitizenAccountUnlock = /unlock-account/.test(props.location.pathname);

  if (
    isEmpty ||
    location.pathname === Paths.ACCOUNT_UNLOCK_EMAIL ||
    location.pathname === Paths.FORGOT_USERNAME ||
    location.pathname === Paths.EMAIL_PASSWORD_RESET ||
    isCitizenAccountActivation ||
    isCitizenAccountUnlock
  ) {
    return (
      <Header
        key="application-header"
        data-testid={ID.APPLICATION_HEADER_COMPONENT_ID}
        logo={<img alt="agency logo" aria-hidden id="sso-verifier-header" src={logo} />}
        menuHidden
        title={formatMessage(translations.headerLandingPageTitle)}
        titleHoverText={formatMessage(translations.headerHomeLabel)}
        type="scrollable"
      >
        <span />
      </Header>
    );
  }
  return (
    <Header
      key="application-header"
      data-testid={ID.APPLICATION_HEADER_COMPONENT_ID}
      link={<RouterLink to={PATHS.HOME} />}
      logo={
        <img
          alt="agency logo"
          aria-hidden
          data-testid={ID.APPLICATION_HEADER_HOME_LINK_ID}
          id={ID.APPLICATION_HEADER_HOME_LINK_ID}
          src={logo}
        />
      }
      menuButtonTitle={formatMessage(translations.headerMenuLabel)}
      title={formatMessage(translations.headerLandingPageTitle)}
      titleHoverText={formatMessage(translations.headerHomeLabel)}
      type="scrollable"
    >
      <PrimaryNavigation type="scrollable">
        {isYourAccountTabActive(currentUrlPathname) && (
          <Tabs
            key="primary-tabs"
            ariaLabel="primary-tabs"
            current={getActivePrimaryNavigationTabId(currentUrlPathname)}
            scrollable
          >
            <Tab
              data-testid={NAVIGATION_HEADER_TABS.DASHBOARD.ID}
              id={NAVIGATION_HEADER_TABS.DASHBOARD.ID}
              label={formatMessage(translations.headerDashboardLabel)}
              link={<RouterLink to={PATHS.ACCOUNT} />}
            />
            <Tab
              data-testid={NAVIGATION_HEADER_TABS.YOUR_BENEFITS.ID}
              id={NAVIGATION_HEADER_TABS.YOUR_BENEFITS.ID}
              label={formatMessage(translations.headerYourBenefitsLabel)}
              link={<RouterLink to={PATHS.BENEFITS} />}
            />
            <Tab
              data-testid={NAVIGATION_HEADER_TABS.PROFILE.ID}
              id={NAVIGATION_HEADER_TABS.PROFILE.ID}
              label={formatMessage(translations.headerProfileText)}
              link={<RouterLink to={PATHS.PROFILE} />}
            />
            {/* ACES customization - Add doc upload */}
            {isALinkedOrStandardUser() && (
              <Tab
                id={NAVIGATION_HEADER_TABS.VERIFICATIONS.ID}
                label={formatMessage(translations.headerYourVerificationsText)}
                link={<RouterLink to={Paths.VERIFICATIONS.ROOT} />}
              />
            )}
            {/* ACES customization - disable unused features */}
            {/* <Tab
              data-testid={NAVIGATION_HEADER_TABS.PROFILE.ID}
              id={NAVIGATION_HEADER_TABS.PROFILE.ID}
              label={formatMessage(translations.headerProfileText)}
              link={<RouterLink to={PATHS.PROFILE} />}
            />

            {isALinkedUser() && (
              <Tab
                data-testid={NAVIGATION_HEADER_TABS.NOTICES.ID}
                id={NAVIGATION_HEADER_TABS.NOTICES.ID}
                label={formatMessage(translations.headerNoticesLabel)}
                link={<RouterLink to={PATHS.NOTICES.ROOT} />}
              />
            )}
            {isAppealsEnabled && appealsTab} */}
          </Tabs>
        )}
      </PrimaryNavigation>
      <SecondaryNavigation type="scrollable">
        <Tabs
          key="secondary-tabs"
          ariaLabel="secondary-tabs"
          className="wds-u-mr--small"
          current={getActiveSecondaryNavigationTabId(currentUrlPathname)}
        >
          <Tab
            data-testid={NAVIGATION_HEADER_TABS.HOME.ID}
            id={NAVIGATION_HEADER_TABS.HOME.ID}
            label={formatMessage(translations.headerHomeLabel)}
            link={<RouterLink to={PATHS.HOME} />}
          />
          <Tab
            data-testid={NAVIGATION_HEADER_TABS.BENEFITS.ID}
            id={NAVIGATION_HEADER_TABS.BENEFITS.ID}
            label={formatMessage(translations.headerBenefitsLabel)}
            link={<RouterLink to={PATHS.APPLY} />}
          />
          {userProfile && (
            <Tab
              data-testid={NAVIGATION_HEADER_TABS.YOUR_ACCOUNT.ID}
              id={NAVIGATION_HEADER_TABS.YOUR_ACCOUNT.ID}
              label={formatMessage(translations.headerYourAccountLabel)}
              link={<RouterLink to={PATHS.ACCOUNT} />}
            />
          )}
        </Tabs>
        {/* Show profile menu or signed out menu */}
        {userProfile ? (
          <Button
            key="logout"
            category="secondary"
            className="wds-u-mr--small"
            data-testid={ID.PROFILE_MENU_LOGOUT_BUTTON_ID}
            onClick={() => {
              Authentication.logout(() => {
                // 1667 & 1658 - In CE, to access protected routes such as Benefits/Forgot password etc you either
                // need to be logged in as a standard user or public citizen. When logging out, there is a
                // delay in which the system automatically signs you out as a standard user and back in as public citizen
                // and it will cause errors when trying to access protected routes in this time frame. This API
                // call manually logs in as public citizen upon user sign out to avoid this error
                Authentication.loginAsPublicCitizen(loginAsPublicCitizenCallback);
                console.log('Logging out user...');
              });
            }}
            size="small"
            style={marginDirection}
          >
            {formatMessage(translations.headerLogOutText)}
          </Button>
        ) : (
          !hasErrorBeenCaught && <SignInMenuComponent />
        )}
      </SecondaryNavigation>
      {!userProfile && !hasErrorBeenCaught && (
        <Button
          key="login"
          category="link"
          className="wds-u-hide--nav-width-up"
          data-testid={ID.APPLICATION_HEADER_LOGIN_LINK_ID}
          onClick={e => {
            // This is the mobile screen login button
            e.preventDefault();
            props.history.push({
              pathname: PATHS.LOGIN,
              params: { previousPathname: location.pathname },
            });
          }}
          style={marginDirection}
        >
          {formatMessage(translations.headerLogInText)}
        </Button>
      )}
    </Header>
  );
};

/**
 * The ApplicationHeaderComponent properties.
 *
 * See https://reactjs.org/docs/react-component.html#props
 *
 * @memberof ApplicationHeaderComponent
 */
ApplicationHeaderComponent.propTypes = {
  hasErrorBeenCaught: PropTypes.bool,
  history: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  isALinkedOrStandardUser: PropTypes.func.isRequired,
  isAppealsEnabled: PropTypes.bool.isRequired,
  isEmpty: PropTypes.bool,
  isUserCanAppeal: PropTypes.bool,
  location: PropTypes.object.isRequired,
  profile: PropTypes.instanceOf(ProfileModel),
};

/**
 * The ApplicationHeaderComponent properties.
 *
 * See https://reactjs.org/docs/react-component.html#defaultprops
 *
 * @memberof ApplicationHeaderComponent
 */
ApplicationHeaderComponent.defaultProps = {
  hasErrorBeenCaught: false,
  isEmpty: false,
  isUserCanAppeal: false,
  profile: null,
};

export default withRouter(injectIntl(ApplicationHeaderComponent));
